/**
 * WebSocket client: hello handshake, idempotent commands (cmdId kept until acked, re-sent after
 * reconnect), reconnect with token, heartbeat, server-clock offset. State frames replace store.
 */
import type { ClientCommand, ServerFrame } from '@snw/protocol';
import { PROTOCOL_VERSION } from '@snw/protocol';
import { connection, lastEvent, priv, pub, safeSession, serverOffset, showToast, route, seatOption } from '../state/store';
import { publicPlay } from './api';

type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;
export type CmdInput = DistributiveOmit<ClientCommand, 'cmdId'>;
type Pending = { cmd: ClientCommand; resolve: (f: Extract<ServerFrame, { kind: 'ack' }>) => void; sentAt: number };
const CLIENT_BUILD = (import.meta.env.VITE_BUILD as string | undefined) ?? 'dev';

export class GameSocket {
  private ws: WebSocket | null = null;
  private pending = new Map<string, Pending>();
  private token: string | null = null;
  private playerId: string | null = null;
  private code: string | null = null;
  private attempts = 0;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private closedByUser = false;
  private reseated = false;

  connectWithTicket(code: string, ticket: string): void { this.code = code; this.closedByUser = false; this.open(`/ws?ticket=${encodeURIComponent(ticket)}`); }
  resume(code: string): boolean {
    const saved = safeSession(`snw_rt_${code}`); if (!saved) return false;
    const [pid, token] = saved.split('|'); if (!pid || !token) return false;
    this.code = code; this.playerId = pid; this.token = token; this.closedByUser = false;
    this.open(this.reconnectUrl()); return true;
  }
  private reconnectUrl(): string { return `/ws?room=${this.code}&player=${encodeURIComponent(this.playerId!)}&token=${encodeURIComponent(this.token!)}`; }

  private open(path: string): void {
    connection.value = this.attempts ? 'reconnecting' : 'connecting';
    const proto = location.protocol === 'https:' ? 'wss' : 'ws';
    const ws = new WebSocket(`${proto}://${location.host}${path}`);
    this.ws = ws;
    ws.onopen = () => { ws.send(JSON.stringify({ kind: 'hello', protocolVersion: PROTOCOL_VERSION, clientBuild: CLIENT_BUILD })); };
    ws.onmessage = (ev) => this.onFrame(JSON.parse(ev.data as string) as ServerFrame);
    ws.onclose = (ev) => {
      if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
      if (this.closedByUser) { connection.value = 'closed'; return; }
      if (ev.code === 4400) { showToast('Please refresh — a new version is live.'); connection.value = 'closed'; return; }
      if (ev.code === 4409) { this.closedByUser = true; this.token = null; connection.value = 'idle'; showToast('The host removed you from the room.'); history.pushState(null, '', '/'); route.value = { name: 'home' }; return; }
      if (ev.code === 4001 || ev.code === 4003 || (ev.code === 1006 && !this.token)) { // 1006 before welcome: the connection never came up (seen under a 100 conn/s ramp)
        const seat = seatOption.value; // public play: the table filled or started before we arrived → the service seats us at the next one (once)
        if (seat && !this.token && !this.reseated) { this.reseated = true; publicPlay.join(seat.key).then((r) => { this.reseated = false; this.connectWithTicket(r.code, r.ticket); history.replaceState(null, '', `/r/${r.code}`); route.value = { name: 'room', code: r.code }; }).catch(() => { this.reseated = false; connection.value = 'closed'; showToast('That table just started — pick another seat'); history.pushState(null, '', '/'); route.value = { name: 'home' }; }); return; }
        connection.value = 'closed'; showToast(ev.code === 4003 ? 'This room is full or has ended.' : 'Could not rejoin this room.'); return;
      }
      if (!this.token) { connection.value = 'closed'; return; }
      const delay = Math.min(8000, 400 * 2 ** Math.min(this.attempts, 4)) + Math.random() * 300;
      this.attempts += 1; connection.value = 'reconnecting';
      setTimeout(() => { if (!this.closedByUser) this.open(this.reconnectUrl()); }, delay);
    };
    ws.onerror = () => { /* onclose follows */ };
  }

  private onFrame(f: ServerFrame): void {
    switch (f.kind) {
      case 'welcome':
        this.attempts = 0; this.playerId = f.playerId; this.token = f.reconnectToken; connection.value = 'open';
        safeSession(`snw_rt_${this.code}`, `${f.playerId}|${f.reconnectToken}`);
        serverOffset.value = f.serverNow - Date.now();
        pub.value = f.public; priv.value = f.private;
        if (!this.pingTimer) this.pingTimer = setInterval(() => this.ws?.readyState === 1 && this.ws.send(JSON.stringify({ kind: 'ping', t: Date.now() })), 20000);
        for (const p of this.pending.values()) this.ws?.send(JSON.stringify({ kind: 'cmd', cmd: p.cmd })); // idempotent re-send
        break;
      case 'state': pub.value = f.public; priv.value = f.private; break;
      case 'event': lastEvent.value = { event: f.event, at: f.at }; break;
      case 'ack': { const p = this.pending.get(f.cmdId); if (p) { this.pending.delete(f.cmdId); p.resolve(f); } break; }
      case 'pong': serverOffset.value = f.serverNow - Date.now(); break;
      case 'error': if (f.fatal) showToast(f.message || f.code); break;
    }
  }

  send(cmd: CmdInput): Promise<Extract<ServerFrame, { kind: 'ack' }>> {
    const full = { ...cmd, cmdId: newCmdId() } as ClientCommand;
    return new Promise((resolve) => {
      this.pending.set(full.cmdId, { cmd: full, resolve, sentAt: Date.now() });
      if (this.ws?.readyState === 1) this.ws.send(JSON.stringify({ kind: 'cmd', cmd: full }));
    });
  }

  leave(): void { this.closedByUser = true; void this.send({ type: 'room.leave' }); setTimeout(() => this.ws?.close(1000, 'left'), 150); if (this.code) safeSession(`snw_rt_${this.code}`, ''); }
  /** leave the current room and join another with a fresh ticket (public play: "meet new people"). The old socket is detached first so its close handler and the pending leave never reach the new room. */
  switchTo(code: string, ticket: string): void {
    const old = this.ws; if (this.code) safeSession(`snw_rt_${this.code}`, '');
    if (old) { old.onclose = null; old.onmessage = null; if (old.readyState === 1) { try { old.send(JSON.stringify({ kind: 'cmd', cmd: { type: 'room.leave', cmdId: newCmdId() } })); } catch { /* closing anyway */ } } setTimeout(() => old.close(1000, 'left'), 100); }
    if (this.pingTimer) { clearInterval(this.pingTimer); this.pingTimer = null; }
    this.pending.clear(); this.token = null; this.playerId = null; this.attempts = 0; pub.value = null; priv.value = null;
    this.connectWithTicket(code, ticket);
  }
  get id(): string | null { return this.playerId; }
}

function newCmdId(): string { const a = new Uint8Array(12); crypto.getRandomValues(a); return Array.from(a, (b) => b.toString(16).padStart(2, '0')).join(''); }
export const socket = new GameSocket();
